// src/pages/EmployeeOffer/EmployeeOfferRegister.tsx
import { acquireTokenForApi } from '@/authConfig';
import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { Row, Col, Form, Button, Alert } from 'react-bootstrap';
import SuccessModal from '@/components/SuccessModal';
import './EmployeeOfferRegister.scss';

const EmployeeOfferRegister: React.FC = () => {
  const { accounts } = useMsal();
  const currentUser = accounts[0];

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (currentUser) {
      const name = currentUser.name?.split(' ') || [];
      setFirstName(name[0] || '');
      setLastName(name.slice(1).join(' ') || '');
      setEmail(currentUser.username || '');
    }
  }, [currentUser]);

  const handleAccountNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    setAccountNumber(value);
    
    // Validate first character as user types
    if (value.length > 0 && value[0] !== '5') {
      setErrors(['The first character of your account number should be "5".']);
    } else {
      setErrors([]);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!accountNumber || accountNumber.trim().length === 0) {
      newErrors.push('Your account number is missing! Please enter it then click submit.');
    } else if (accountNumber.length !== 9) {
      newErrors.push('Your account number should be 9 digits long! Please double check what you entered.');
    } else if (accountNumber[0] !== '5') {
      newErrors.push('Your account number should start with a 5! Please double check what you entered.');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const token = await acquireTokenForApi();
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/employeeoffer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          employeeEmail: email.trim(),
          employeeFirstName: firstName.trim(),
          employeeLastName: lastName.trim(),
          title: accountNumber.trim(),
        }),
      });

      if (response.ok) {
        setShowSuccess(true);
        setTimeout(() => {
          window.location.href = 'https://www.enmax.com';
        }, 5000);
      } else {
        setErrors(['Failed to submit your registration. Please try again or contact GoToMarket@enmax.com.']);
      }
    } catch (error) {
      console.error('Error submitting employee offer:', error);
      setErrors(['An error occurred while submitting. Please try again or contact GoToMarket@enmax.com.']);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="employee-offer-register legacy-form-page">
      <div className="legacy-form">
        <h3 className="mb-4">Get your ENMAX Employee Easymax® bill credit</h3>
        
        <p><strong>Working at ENMAX has its perks–and this is one of them!</strong></p>
        
        <p>If you are a permanent or limited-term employee and have an Easymax plan, you're eligible for an annual bill credit:</p>
        <ul>
          <li>$50 if you are signed up for electricity or natural gas with Easymax</li>
          <li>$100 if you have both electricity and natural gas on the same Easymax plan</li>
        </ul>

        <h4 className="mt-4">How it works</h4>
            <ul>
              <li>Register now using the form below</li>
              <li>You'll see the credit on your next bill after you register</li>
              <li>You'll continue to see it annually on the same month going forward</li>
            </ul>

            <h4 className="mt-4">Don't have any Easymax plan yet?</h4>
            <p>
              No problem-
              <a href="https://www.enmax.com/electricity-and-natural-gas/easymax" target="_blank" rel="noopener noreferrer">
                sign up for Easymax
              </a>, then return to this page and fill out the form below.
            </p>

            <h4 className="mt-5 mb-3">Enter your details to register for your credit:</h4>

            {errors.length > 0 && (
              <Alert variant="danger">
                {errors.map((error, idx) => (
                  <div key={idx}>{error}</div>
                ))}
                {errors.some(e => e.includes('questions')) && (
                  <div className="mt-2">
                    If you have questions, please email <a href="mailto:GoToMarket@enmax.com">GoToMarket@enmax.com</a>.
                  </div>
                )}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">First name:</Form.Label>
                    <Form.Control
                      type="text"
                      value={firstName}
                      disabled
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Last name:</Form.Label>
                    <Form.Control
                      type="text"
                      value={lastName}
                      disabled
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">Email:</Form.Label>
                    <Form.Control
                      type="email"
                      value={email}
                      disabled
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold">ENMAX Energy Account Number:</Form.Label>
                    <Form.Control
                      type="text"
                      value={accountNumber}
                      onChange={handleAccountNumberChange}
                      maxLength={9}
                      placeholder="Enter your 9-digit account number"
                    />
                    <Form.Text className="text-muted">
                      Please enter your account number starting from the 5.
                    </Form.Text>
                  </Form.Group>

                  <Button 
                    variant="success" 
                    size="lg" 
                    type="submit"
                    disabled={submitting}
                    className="mt-3"
                  >
                    {submitting ? 'Submitting...' : 'Submit'}
                  </Button>
                </Col>

                <Col md={6} className="d-none d-md-block">
                  <img 
                    src="/Images/ENMAX_BA_Example.jpg" 
                    alt="ENMAX Bill Account Example" 
                    className="img-fluid"
                  />
                </Col>
              </Row>
            </Form>

            <div className="mt-5 pt-4 border-top">
              <p>
                If you have any questions about this employee offer, please email{' '}
                <a href="mailto:GoToMarket@enmax.com">GoToMarket@enmax.com</a>.
              </p>

              <p className="small text-muted mt-4">
                By signing up for the offer, you consent to ENMAX Energy Corporation, ENMAX Corporation and ENMAX Encompass Inc. 
                exchanging your personal information only as required to administer the employee offer. ENMAX Energy will also use 
                aggregate information in communications and for reporting purposes.
              </p>

              <p className="small text-muted">
                *Must be a Permanent or Limited Term employee and on a retail plan with ENMAX Energy for credit to be applied. 
                Contractors are not eligible. Employees on the Rate of Last Resort are not eligible. Offer subject to change or 
                cancellation without notice. | ®ENMAX Corporation
              </p>

              <p className="small">
                <strong>
                  Customers are free to purchase natural gas services or electricity services from a retailer of their choice. 
                  For a list of retailers, visit ucahelps.alberta.ca or call 310-4822 (toll free in Alberta).
                </strong>
              </p>
            </div>
          </div>

          <SuccessModal
            show={showSuccess}
            title="Account number submitted"
            message="Your account number has been submitted. You should receive a confirmation email shortly."
            autoCloseSeconds={5}
            onRedirect={() => {
              window.location.href = 'https://www.enmax.com';
            }}
            onClose={() => {
              window.location.href = 'https://www.enmax.com';
            }}
            closeText="Close"
          />
        </div>
      );
    };

    export default EmployeeOfferRegister;
